import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Lock, Mail, Loader2, LogOut, Users, Building2, ArrowLeft, Download } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const TOKEN_KEY = "bgex_admin_token";

const StatCard = ({ icon: Icon, value, label, testid }) => (
  <div className="bg-[#0F172A] border border-[#1E293B] rounded-[20px] p-5" data-testid={testid}>
    <div className="w-9 h-9 rounded-[10px] bg-[#292414] flex items-center justify-center mb-3">
      <Icon className="w-5 h-5 text-[#D4AF37]" />
    </div>
    <div className="font-head font-black text-3xl text-[#F8FAFC]">{value}</div>
    <div className="font-body text-sm text-[#94A3B8] mt-0.5">{label}</div>
  </div>
);

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 gold-spot">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[#0F172A]/70 backdrop-blur-xl border border-[#1E293B] rounded-[20px] p-8"
      >
        <div className="flex items-center gap-2.5 mb-8">
          <ShieldCheck className="w-6 h-6 text-[#D4AF37]" />
          <span className="font-head font-black tracking-[0.18em] text-[#F8FAFC]">
            BODYGUARD<span className="text-[#D4AF37]"> EXP</span>
          </span>
        </div>
        <h1 className="font-head font-black text-2xl text-[#F8FAFC]">Admin Access</h1>
        <p className="font-body text-sm text-[#94A3B8] mt-2">Sign in to view the founding waitlist.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="flex items-center gap-3 bg-transparent border border-[#1E293B] rounded-xl px-4 focus-within:border-[#D4AF37] transition-colors">
            <Mail className="w-5 h-5 text-[#94A3B8]" />
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email" placeholder="admin@bodyguardexp.com"
              className="flex-1 bg-transparent outline-none border-0 text-[#F8FAFC] font-body py-3.5 placeholder:text-[#94A3B8] focus:ring-0"
            />
          </div>
          <div className="flex items-center gap-3 bg-transparent border border-[#1E293B] rounded-xl px-4 focus-within:border-[#D4AF37] transition-colors">
            <Lock className="w-5 h-5 text-[#94A3B8]" />
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password" placeholder="••••••••"
              className="flex-1 bg-transparent outline-none border-0 text-[#F8FAFC] font-body py-3.5 placeholder:text-[#94A3B8] focus:ring-0"
            />
          </div>
          <button
            type="submit" disabled={loading} data-testid="admin-login-btn"
            className="w-full font-head font-bold bg-[#D4AF37] text-[#050914] h-[52px] rounded-xl hover:bg-[#E4C569] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
          </button>
        </form>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 font-body text-sm text-[#94A3B8] hover:text-[#D4AF37] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
      </motion.div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, agents: 0, security: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { role: filter };
      const [list, s] = await Promise.all([
        api.get("/admin/waitlist", { ...authHeader, params }),
        api.get("/admin/stats", authHeader),
      ]);
      setRows(list.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const header = ["Name", "Email", "Phone", "Role", "Joined"];
    const lines = rows.map((r) => [r.name, r.email, r.phone, r.role, r.created_at].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "bgex-waitlist.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const filters = [
    { id: "all", label: "All" },
    { id: "agent", label: "Agents" },
    { id: "security", label: "Companies" },
  ];

  return (
    <div className="min-h-screen max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2.5">
          <ShieldCheck className="w-6 h-6 text-[#D4AF37]" />
          <span className="font-head font-black tracking-[0.18em] text-[#F8FAFC]">
            BODYGUARD<span className="text-[#D4AF37]"> EXP</span>
          </span>
          <span className="font-body text-sm text-[#94A3B8] ml-2">/ Waitlist</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={exportCsv} data-testid="export-csv-btn" className="font-head font-bold text-sm bg-transparent text-[#D4AF37] border-[1.5px] border-[#D4AF37] px-4 h-10 rounded-xl hover:bg-[#292414] transition-colors inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={onLogout} data-testid="admin-logout-btn" className="font-head font-bold text-sm bg-[#0F172A] text-[#CBD5E1] border border-[#1E293B] px-4 h-10 rounded-xl hover:border-[#334155] transition-colors inline-flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        <StatCard icon={Users} value={stats.total} label="Total signups" testid="stat-total" />
        <StatCard icon={Building2} value={stats.agents} label="Real estate agents" testid="stat-agents" />
        <StatCard icon={ShieldCheck} value={stats.security} label="Security companies" testid="stat-security" />
      </div>

      <div className="flex items-center gap-2 mt-10 bg-[#0F172A] border border-[#1E293B] rounded-full p-1 w-fit">
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
            className={`font-head font-bold text-sm px-4 py-2 rounded-full transition-colors ${filter === f.id ? "bg-[#D4AF37] text-[#050914]" : "text-[#94A3B8] hover:text-[#F8FAFC]"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6 bg-[#0F172A] border border-[#1E293B] rounded-[20px] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="waitlist-table">
            <thead>
              <tr className="border-b border-[#1E293B]">
                {["Name", "Email", "Phone", "Role", "Joined"].map((h) => (
                  <th key={h} className="font-head font-bold text-xs uppercase tracking-wider text-[#94A3B8] px-6 py-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-6 py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37] mx-auto" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-16 text-center font-body text-[#94A3B8]">No signups yet.</td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#1E293B] last:border-0 hover:bg-[#1E293B]/30 transition-colors" data-testid="waitlist-row">
                    <td className="px-6 py-4 font-body text-[#F8FAFC] font-medium">{r.name}</td>
                    <td className="px-6 py-4 font-body text-[#CBD5E1]">{r.email}</td>
                    <td className="px-6 py-4 font-body text-[#CBD5E1]">{r.phone}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold font-body border ${r.role === "agent" ? "text-[#3B82F6] border-[#3B82F6] bg-[#3B82F6]/10" : "text-[#D4AF37] border-[#D4AF37] bg-[#D4AF37]/10"}`}>
                        {r.role === "agent" ? "Agent" : "Security"}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-body text-[#94A3B8] text-sm whitespace-nowrap">{new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  return authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />;
}
